import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import MarketingLayout from '../components/marketing/MarketingLayout';
import { api } from '../lib/api';

const PERSONAL_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com'];

// Condensed "what a demo covers" — the full capability story lives on /features.
const DEMO_POINTS = [
  'Marketing, HMDA, fair lending, CRA, and exam packaging — connected in one platform',
  'Fair lending + CRA on your whole loan book (HMDA and non-HMDA / ALERT-ILDR)',
  'One-click examiner-ready packages and board reports',
  'Tailored to your institution’s asset size, regulators, and workflow',
];

export default function DemoRequestPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    institutionName: '',
    title: '',
    notes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState('');

  const submitMutation = useMutation({
    mutationFn: (data: typeof formData) => api.post('/contact/demo-request', data),
    onSuccess: () => setSubmitted(true),
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Something went wrong. Please try again.');
    },
  });

  const validateEmail = (email: string) => {
    const domain = email.split('@')[1]?.toLowerCase();
    if (domain && PERSONAL_DOMAINS.includes(domain)) {
      setEmailError('Please use your work email so we can tailor the demo to your institution.');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(formData.email)) return;
    submitMutation.mutate(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'email') setEmailError('');
  };

  return (
    <MarketingLayout>
      <section className="section">
        <div className="container">
          <div className="demo-grid">
            {/* Left — what a demo is */}
            <div className="demo-intro reveal">
              <span className="kicker">§ REQUEST A DEMO</span>
              <h1 className="h2 balance" style={{ marginTop: '1.25rem', maxWidth: '15ch' }}>
                See Clarid against your institution&rsquo;s real compliance work.
              </h1>
              <p className="lead" style={{ marginTop: '1.25rem', maxWidth: '46ch' }}>
                A guided walkthrough with our team &mdash; far more than a sandbox can show. Tell us
                about your institution and we&rsquo;ll shape the demo around it.
              </p>
              <ul className="demo-points">
                {DEMO_POINTS.map((p) => (
                  <li key={p}><CheckIcon aria-hidden="true" /><span>{p}</span></li>
                ))}
              </ul>
              <p className="meta" style={{ marginTop: '2rem' }}>
                Built by a community banker &middot; Validated by former FDIC and Federal Reserve examiners
              </p>
            </div>

            {/* Right — the form (or success state) */}
            <div className="demo-form-col reveal">
              {submitted ? (
                <div className="form-card" style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      width: '3.25rem', height: '3.25rem', margin: '0 auto 1.25rem',
                      borderRadius: '999px', background: 'var(--teal-pill)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <CheckIcon aria-hidden="true" style={{ width: '1.6rem', height: '1.6rem', color: 'var(--teal-hover)' }} />
                  </div>
                  <h2 className="h3">Thanks &mdash; you&rsquo;ll hear from us within one business day.</h2>
                  <p className="body" style={{ marginTop: '0.6rem' }}>
                    We&rsquo;ll reach out to schedule a personalized demo based on your institution&rsquo;s needs.
                  </p>
                </div>
              ) : (
                <form className="form-card" onSubmit={handleSubmit} noValidate>
                  <div className="field">
                    <label htmlFor="fullName">Full name</label>
                    <input id="fullName" name="fullName" type="text" required
                      value={formData.fullName} onChange={handleChange} placeholder="Jane Smith" />
                  </div>

                  <div className={`field${emailError ? ' field--error' : ''}`}>
                    <label htmlFor="email">Work email</label>
                    <input id="email" name="email" type="email" required
                      value={formData.email} onChange={handleChange}
                      onBlur={() => formData.email && validateEmail(formData.email)}
                      placeholder="jane@firstnationalbank.com" />
                    {emailError && <p className="field-error">{emailError}</p>}
                  </div>

                  <div className="field">
                    <label htmlFor="institutionName">Institution name</label>
                    <input id="institutionName" name="institutionName" type="text" required
                      value={formData.institutionName} onChange={handleChange} placeholder="First National Bank" />
                  </div>

                  <div className="field">
                    <label htmlFor="title">Title / role</label>
                    <input id="title" name="title" type="text" required
                      value={formData.title} onChange={handleChange} placeholder="VP of Compliance" />
                  </div>

                  <div className="field">
                    <label htmlFor="notes">Anything specific you&rsquo;d like to see? (optional)</label>
                    <textarea id="notes" name="notes" rows={3}
                      value={formData.notes} onChange={handleChange}
                      placeholder="e.g. fair-lending exam prep, CRA gap analysis, non-HMDA loan analysis..." />
                  </div>

                  <button type="submit" className="btn btn-primary btn-block" disabled={submitMutation.isPending}>
                    {submitMutation.isPending ? 'Submitting…' : 'Request a demo'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
